/**
 * Auth Context for RBAC
 * 
 * Manages authentication state including user role and tenant information.
 * Provides role checking utilities and auth status.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAuthStatus, logoutSession } from '../services/api';
import { msalInstance } from '../auth/msal';
import { STORAGE_KEY as COOKIE_CONSENT_KEY } from '../lib/cookieConsent';
import { ROLES } from '../lib/roles';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  // Check authentication status
  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      const status = await getAuthStatus();

      if (status.authenticated && status.user) {
        setAuthenticated(true);
        setUser({
          id: status.user.id,
          email: status.user.email,
          username: status.user.username,
          fullName: status.user.full_name,
          role: status.user.role,
          tenantId: status.user.tenant_id,
          isActive: status.user.is_active,
        });
      } else {
        setAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Set auth state directly from a login response (avoids cross-origin cookie issues
  // where /auth/status can't receive the session cookie on XHR requests)
  const setAuthFromLoginResponse = useCallback((userData) => {
    if (userData) {
      setAuthenticated(true);
      setUser({
        id: userData.id,
        email: userData.email,
        username: userData.username,
        fullName: userData.full_name,
        role: userData.role,
        tenantId: userData.tenant_id,
        isActive: userData.is_active,
      });
      setLoading(false);
    }
  }, []);

  // Initial auth check
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Logout handler
  const logout = useCallback(async () => {
    try {
      await logoutSession();
    } catch (error) {
      console.error('Logout session call failed:', error);
    }

    // Clear MSAL accounts to prevent auto-relogin loops
    try {
      await msalInstance.clearCache();
    } catch (msalErr) {
      console.warn('Failed to clear MSAL account cache', msalErr);
    }

    // Wipe ALL localStorage (MSAL tokens, user_id, app data, etc.)
    // Cookie consent is a browser-level choice, not an account-level one,
    // so it is deliberately snapshotted and restored across the wipe —
    // otherwise the consent banner re-prompts after every logout.
    try {
      let consentSnapshot = null;
      try {
        consentSnapshot = localStorage.getItem(COOKIE_CONSENT_KEY);
      } catch {
        // ignore — consent just won't be restored
      }
      localStorage.clear();
      if (consentSnapshot) {
        try {
          localStorage.setItem(COOKIE_CONSENT_KEY, consentSnapshot);
        } catch {
          // ignore — the consent cookie mirror still covers this browser
        }
      }
      console.log('🗑️ Cleared all localStorage on logout');
    } catch (e) {
      console.warn('Failed to clear localStorage', e);
    }

    // Clear MSAL-related cookies so stale auth state cannot repopulate the cache
    try {
      document.cookie.split(';').forEach(cookie => {
        const name = cookie.split('=')[0].trim();
        if (name.startsWith('msal.') || name.startsWith('msal|')) {
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        }
      });
    } catch (e) {
      console.warn('Failed to clear MSAL cookies', e);
    }

    // Signal to LoginNew that we just logged out so it won't auto-redirect
    try {
      sessionStorage.setItem('loggedOut', 'true');
    } catch (e) {
      // sessionStorage not available
    }

    setAuthenticated(false);
    setUser(null);
  }, []);

  // Role checking utilities.
  // Exact-equality with no hierarchy: a global_admin does NOT satisfy isAdmin(), which
  // is why callers that mean "either" write `isAdmin() || isGlobalAdmin()`.
  const isAdmin = useCallback(() => {
    return user?.role === ROLES.ADMIN;
  }, [user]);

  const isGlobalAdmin = useCallback(() => {
    return user?.role === ROLES.GLOBAL_ADMIN;
  }, [user]);

  const isRecruiter = useCallback(() => {
    return user?.role === ROLES.RECRUITER;
  }, [user]);

  const isCandidate = useCallback(() => {
    return user?.role === ROLES.CANDIDATE;
  }, [user]);

  const hasRole = useCallback((requiredRole) => {
    return user?.role === requiredRole;
  }, [user]);

  const hasAnyRole = useCallback((roles) => {
    return roles.includes(user?.role);
  }, [user]);

  const value = {
    user,
    authenticated,
    loading,
    checkAuth,
    setAuthFromLoginResponse,
    logout,
    isAdmin,
    isGlobalAdmin,
    isRecruiter,
    isCandidate,
    hasRole,
    hasAnyRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
